import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import type { User } from "@repo/reframe-interfaces";
import { API } from "@reframe-mobile/api";

type AuthStatus = "idle" | "loading" | "success" | "error";

interface AuthState {
  user: User | null;
  status: AuthStatus;
}

interface AuthActions {
  init: () => Promise<void>;
  setUser: (user: User | null) => void;
  login: (email: string, password: string) => Promise<User>;
  register: (
    email: string,
    password: string,
    nickname: string,
  ) => Promise<User>;
  logout: () => Promise<void>;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    immer((set) => ({
      user: null,
      status: "idle",

      init: async () => {
        set((state) => {
          state.status = "loading";
        });
        try {
          const { data } = await API.auth.me();
          set((state) => {
            state.user = data;
            state.status = "success";
          });
        } catch {
          set((state) => {
            state.user = null;
            state.status = "success";
          });
        }
      },

      setUser: (user) => {
        set((state) => {
          state.user = user;
          state.status = "success";
        });
      },

      login: async (email, password) => {
        const { data } = await API.auth.login({ email, password });
        const user = data as unknown as User;
        set((state) => {
          state.user = user;
          state.status = "success";
        });
        return user;
      },

      register: async (email, password, nickname) => {
        const { data } = await API.auth.register({
          email,
          password,
          nickname,
        });
        const user = data as unknown as User;
        set((state) => {
          state.user = user;
          state.status = "success";
        });
        return user;
      },

      logout: async () => {
        try {
          await API.auth.logout();
        } finally {
          set((state) => {
            state.user = null;
            state.status = "success";
          });
        }
      },
    })),
    { name: "auth" },
  ),
);
